// src/features/dashboard/utils/date.utils.ts

/* ============================================================
   CREATE LOCAL DATE

   IMPORTANT:
   Date-only dashboard values must stay in the browser's
   local timezone.

   DO NOT use:
       new Date('2026-09-13')
       date.toISOString()

   for date-only values because they can cause a
   timezone-related previous-day shift.
============================================================ */

export const createLocalDate = (
  year: number,
  month: number,
  day: number,
): Date => {
  return new Date(
    year,
    month,
    day,
    0,
    0,
    0,
    0,
  );
};


/* ============================================================
   NORMALIZE LOCAL DATE

   Keeps the same local calendar date and removes the
   time portion.
============================================================ */

export const normalizeDate = (
  date: Date,
): Date => {
  return createLocalDate(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
};


/* ============================================================
   GET TODAY

   Uses the browser's LOCAL calendar date.

   Example:
   If the local date is:

       13-Sep-2026

   this returns:

       13-Sep-2026

   It will NOT convert the date to UTC.
============================================================ */

export const getToday = (): Date => {
  const now = new Date();

  return createLocalDate(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
};


/* ============================================================
   FORMAT DATE FOR API

   Example:

       13-Sep-2026

   becomes:

       2026-09-13

   IMPORTANT:
   Uses getFullYear(), getMonth() and getDate().
   It does NOT use toISOString().
============================================================ */

export const formatApiDate = (
  date: Date,
): string => {
  const normalizedDate =
    normalizeDate(date);

  const year =
    normalizedDate.getFullYear();

  const month =
    String(
      normalizedDate.getMonth() + 1,
    ).padStart(
      2,
      '0',
    );

  const day =
    String(
      normalizedDate.getDate(),
    ).padStart(
      2,
      '0',
    );

  return `${year}-${month}-${day}`;
};


/* ============================================================
   FORMAT DATE ONLY

   Example:

       13-Sep-2026

   becomes:

       2026-09-13
============================================================ */

export const formatDateOnly = (
  date: Date,
): string => {
  return formatApiDate(date);
};


/* ============================================================
   FORMAT DISPLAY DATE

   Example:

       13-Sep-2026
============================================================ */

export const formatDisplayDate = (
  date: Date,
): string => {
  const normalizedDate =
    normalizeDate(date);

  const day =
    String(
      normalizedDate.getDate(),
    ).padStart(
      2,
      '0',
    );

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const month =
    monthNames[
      normalizedDate.getMonth()
    ];

  const year =
    normalizedDate.getFullYear();

  return `${day}-${month}-${year}`;
};


/* ============================================================
   FORMAT DATE RANGE

   Example:

       07-Sep-2026 - 13-Sep-2026

   or:

       13-Sep-2026 - 13-Sep-2026
============================================================ */

export const getDateRangeText = (
  fromDate: Date,
  toDate: Date,
): string => {
  return `${formatDisplayDate(
    fromDate,
  )} - ${formatDisplayDate(
    toDate,
  )}`;
};


/* ============================================================
   ADD / SUBTRACT DAYS

   Example:

       addDays(
         13-Sep-2026,
         -1,
       )

   returns:

       12-Sep-2026
============================================================ */

export const addDays = (
  date: Date,
  days: number,
): Date => {
  const result =
    normalizeDate(date);

  result.setDate(
    result.getDate() + days,
  );

  return normalizeDate(result);
};


/* ============================================================
   START OF MONTH

   Example:

       13-Sep-2026

   becomes:

       01-Sep-2026
============================================================ */

export const startOfMonth = (
  date: Date,
): Date => {
  const normalizedDate =
    normalizeDate(date);

  return createLocalDate(
    normalizedDate.getFullYear(),
    normalizedDate.getMonth(),
    1,
  );
};


/* ============================================================
   END OF MONTH

   Example:

       13-Sep-2026

   becomes:

       30-Sep-2026
============================================================ */

export const endOfMonth = (
  date: Date,
): Date => {
  const normalizedDate =
    normalizeDate(date);

  return createLocalDate(
    normalizedDate.getFullYear(),
    normalizedDate.getMonth() + 1,
    0,
  );
};